import EyeOff from '@/assets/icons/eye-off.svg';
import EyeOn from '@/assets/icons/eye-on.svg';
import AuthLayout from '@/components/AuthLayout';
import AppButton from '@/components/common/AppButton/AppButton';
import AppFormField from '@/components/common/AppFormField';
import { useAuth } from '@/context/AuthContext';
import { yupResolver } from '@hookform/resolvers/yup';
import { CircleAlert } from 'lucide-react';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

const loginSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email'),
  password: yup.string().required('Password is required'),
});

type LoginFormValues = yup.InferType<typeof loginSchema>;

const LoginPage = () => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();

  const methods = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '' },
    resolver: yupResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormValues) => {
    setLoading(true);
    setSubmitError('');

    try {
      await login(data.email, data.password);
      navigate('/');
    } catch {
      setSubmitError('Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Log in to Pool Guy">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col gap-6 md:gap-8"
        >
          {submitError && (
            <div className="flex items-center gap-2 bg-danger/20 text-danger p-2 rounded-lg">
              <CircleAlert />
              {submitError}
            </div>
          )}

          <div className="flex flex-col gap-4">
            <AppFormField<LoginFormValues>
              name="email"
              label="Email Address"
              type="email"
              placeholder="Enter your email"
            />

            <AppFormField<LoginFormValues>
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-grey10 hover:text-dark"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff /> : <EyeOn />}
                </button>
              }
            />

            <div className="text-right mt-[-12px]">
              <a
                href="/forgot-password"
                className="text-sm text-light hover:text-primary"
              >
                Forgot your password?
              </a>
            </div>
          </div>

          <div className="px-8">
            <AppButton type="submit" fullWidth loading={loading} className="">
              Log In
            </AppButton>
          </div>

          <div className="flex items-center justify-center gap-3">
            <div className="w-full h-[1px] bg-grey" />
            <span className="font-semibold text-dark/20">OR</span>
            <div className="w-full h-[1px] bg-grey" />
          </div>

          <div className="px-8">
            <AppButton
              type="button"
              fullWidth
              variant="secondary"
              onClick={() => navigate('/register')}
            >
              Register
            </AppButton>
          </div>
        </form>
      </FormProvider>
    </AuthLayout>
  );
};

export default LoginPage;
